import { api } from "@/lib/api"

import type { CorteReal, PnlRow } from "@/routes/finance/mapping"

/**
 * Forecast con tres escenarios, y el contable que sale de él.
 *
 *   GET /finance/forecast/escenarios   los tres, con sus supuestos en palabras
 *   GET /finance/forecast/proyeccion   el P&L con el escenario aplicado
 *   GET /finance/forecast/impuestos    qué impuestos vienen, por país
 */

export type IdEscenario = "pesimista" | "moderado" | "optimista"

export interface ResumenEscenario {
  id: IdEscenario
  nombre: string
  factor_ingreso: number
  /** Qué supone, EN PALABRAS. Un escenario sin sus supuestos no se discute. */
  supuestos: string
  cuentas_variables: string[]
  cuentas_fijas: string[]
  nota: string
  /** El del catálogo, para poder decir «antes 0,80 · ahora 0,50». */
  factor_por_defecto?: number
  /** Qué supuestos tocó el cliente. Ver `SupuestosDeEscenario`. */
  editados?: string[]
  elasticidades?: Record<string, number>
  retraso_dias?: number | null
  merma_pct?: number | null
}

/**
 * `anio` es obligatorio: los supuestos se declaran POR AÑO (los de 2026 no son
 * los de 2027). Pedirlos sin decir de qué año se habla devolvería los valores
 * de fábrica a un cliente que ya declaró los suyos.
 */
export function fetchEscenarios(anio: number): Promise<{
  escenarios: ResumenEscenario[]
  nota: string
}> {
  return api.get("/finance/forecast/escenarios", { query: { anio } })
}

/**
 * Cuánto del año puede mover el escenario.
 *
 * Es lo primero que hay que decir: con los datos reales de este cliente los
 * tres escenarios dan 461k, 465k y 469k — un ±1%, no un ±20%. No es un fallo
 * de la fórmula: es que su hoja apenas proyecta los meses que quedan. Sin este
 * dato, alguien mira tres cifras casi idénticas y concluye que los escenarios
 * no funcionan.
 */
export interface AlcanceDelEscenario {
  meses_reales: number
  meses_proyectados: number
  ingreso_real: number
  ingreso_proyectado: number
  proporcion_movible: number | null
  aviso: string | null
}

export interface Proyeccion extends Omit<PnlRow, "origen"> {
  escenario: ResumenEscenario
  corte?: CorteReal
  alcance: AlcanceDelEscenario
  /** Las celdas pisadas a mano que entraron en esta cifra. */
  ajustes: AjusteDeCelda[]
  /** `false` en el consolidado: ver `no_ajustable_porque`. */
  ajustable: boolean
  no_ajustable_porque: string | null
}

export function fetchProyeccion(
  anio: number, escenario: IdEscenario, pais?: string,
): Promise<Proyeccion> {
  return api.get("/finance/forecast/proyeccion", {
    query: { anio, escenario, pais: pais || undefined },
  })
}

// ===========================================================================
// EL FORECAST EDITABLE (migración 0025) — punto 6 de la segunda tanda
//
//   GET    /finance/forecast/supuestos   los supuestos de los tres escenarios
//   PUT    /finance/forecast/supuestos   declara uno
//   DELETE /finance/forecast/supuestos   lo devuelve al valor por defecto
//   GET    /finance/forecast/ajustes     las celdas pisadas, con su motivo
//   PUT    /finance/forecast/ajustes     pisa una celda
//   DELETE /finance/forecast/ajustes     la devuelve al cálculo
//   GET    /finance/forecast/caja        el flujo de caja proyectado
//   GET    /finance/forecast/cobranza    cuándo entra la plata
// ===========================================================================

export type TipoDeSupuesto =
  | "factor_ingreso"
  | "elasticidad"
  | "retraso_dias"
  | "merma_pct"

export type BloqueAjustable = "income_statement" | "cashflow" | "cobranza"

/** `todos` es el comodín: el mismo ajuste vale para los tres escenarios. */
export type EscenarioDelAjuste = IdEscenario | "todos"

export interface ElasticidadDeCuenta {
  cuenta: string
  valor: number
  por_defecto: number
  /** Si lo declaró el cliente. La pantalla lo marca: un supuesto tocado a mano
   *  tiene que distinguirse del que trae Marty, o nadie sabe qué se discute. */
  editado: boolean
}

export interface SupuestosDeEscenario {
  escenario: IdEscenario
  nombre: string
  factor_ingreso: number
  factor_por_defecto: number
  retraso_dias: number
  retraso_por_defecto: number
  merma_pct: number
  merma_por_defecto: number
  elasticidades: ElasticidadDeCuenta[]
  editados: string[]
  notas: Record<string, string>
}

export function fetchSupuestos(anio: number): Promise<{
  anio: number
  escenarios: SupuestosDeEscenario[]
  tipos: Record<TipoDeSupuesto, string>
  nota: string
}> {
  return api.get("/finance/forecast/supuestos", { query: { anio } })
}

export function guardarSupuesto(anio: number, body: {
  escenario: IdEscenario
  tipo: TipoDeSupuesto
  cuenta?: string | null
  valor: number
  nota?: string | null
}): Promise<{ escenario: ResumenEscenario; mensaje: string }> {
  return api.put("/finance/forecast/supuestos", body, { query: { anio } })
}

export function borrarSupuesto(anio: number, params: {
  escenario: IdEscenario
  tipo: TipoDeSupuesto
  cuenta?: string | null
}): Promise<{ mensaje: string; escenario: ResumenEscenario }> {
  return api.delete("/finance/forecast/supuestos", {
    query: { anio, escenario: params.escenario, tipo: params.tipo,
             cuenta: params.cuenta || undefined },
  })
}

export interface AjusteDeCelda {
  pais: string
  anio: number
  mes: number
  escenario: EscenarioDelAjuste
  bloque: BloqueAjustable
  cuenta: string
  valor_usd: number
  /** Lo que la proyección daba antes. `null` = no se sabe a dónde vuelve. */
  revierte_a: number | null
  motivo: string
  actualizado_en?: string | null
  actualizado_por?: string | null
}

export function fetchAjustes(
  anio: number, escenario: IdEscenario, pais?: string,
): Promise<{ anio: number; escenario: string; pais: string
            ajustes: AjusteDeCelda[]; total: number }> {
  return api.get("/finance/forecast/ajustes", {
    query: { anio, escenario, pais: pais || undefined },
  })
}

export function guardarAjuste(body: {
  pais: string
  anio: number
  mes: number
  escenario: EscenarioDelAjuste
  bloque: BloqueAjustable
  cuenta: string
  valor_usd: number
  motivo: string
  revierte_a?: number | null
}): Promise<{ valor_usd: number; mensaje: string }> {
  return api.put("/finance/forecast/ajustes", body)
}

export function borrarAjuste(params: {
  pais: string
  anio: number
  mes: number
  escenario: EscenarioDelAjuste
  bloque: BloqueAjustable
  cuenta: string
}): Promise<{ mensaje: string; vuelve_a: number | null }> {
  return api.delete("/finance/forecast/ajustes", { query: params })
}

/** La caja proyectada. Misma forma que `/finance/cashflow`, a propósito. */
export interface CajaProyectada {
  anio: number
  pais: string
  moneda: string
  escenario: ResumenEscenario
  corte?: CorteReal
  entradas: Record<string, number>
  salidas: Record<string, number>
  neto: Record<string, number>
  acumulado: Record<string, number>
  saldo_inicial: number
  caja_bancos: number | null
  acumula_desde: number | null
  saldo_inicial_completo: boolean
  /**
   * EL MES EN QUE LA CAJA CRUZA EL CERO. Lo calcula el servidor, no la
   * pantalla: es la única cifra por la que alguien va a decidir algo esta
   * semana, y dejar que cada vista la derive es cómo dos pantallas acaban
   * señalando meses distintos.
   */
  primer_mes_negativo: number | null
  /**
   * TODOS los meses en rojo, no sólo el primero.
   *
   * «Se queda en negativo desde enero» describía un agujero que empieza y no
   * termina, y con los datos reales la caja cruza el cero seis veces. Quien
   * lee eso busca financiación para el año cuando lo que tiene son baches.
   */
  meses_en_negativo: number[]
  cuentas: Array<{
    account: string
    meses: Record<string, number>
    total: number
    meses_ajustados: number[]
    elasticidad: number
  }>
  ajustes: AjusteDeCelda[]
  ajustable: boolean
  no_ajustable_porque: string | null
  aviso: string | null
  nota: string
}

export function fetchCajaProyectada(
  anio: number, escenario: IdEscenario, pais?: string,
): Promise<CajaProyectada> {
  return api.get("/finance/forecast/caja", {
    query: { anio, escenario, pais: pais || undefined },
  })
}

/**
 * Cuándo entra la plata.
 *
 * DOS SERIES Y NUNCA UNA SOLA: `de_cartera` es lo ya emitido —un hecho— y
 * `de_lo_nuevo` es lo que se facturará —una expectativa—. Sumarlas en una
 * cifra sin decir la proporción es mezclar las dos cosas, y quien la mire para
 * decidir si aguanta el mes estaría apostando sin saberlo.
 */
export interface CobranzaProyectada {
  anio: number
  pais: string
  moneda: string
  escenario: ResumenEscenario
  corte?: CorteReal
  retraso_dias: number
  merma_pct: number
  desfase_meses: number
  dias_credito: number
  de_cartera: Record<string, number>
  de_lo_nuevo: Record<string, number>
  total: Record<string, number>
  cartera: {
    facturas: number
    importe_usd: number
    declarado_incobrable_usd: number
    en_gestion_usd: number
    merma_usd: number
    cobrable_en_el_anio_usd: number
    se_cobra_en_otro_anio_usd: number
    sin_fecha_para_proyectar_usd: number
    sin_fecha_para_proyectar: Array<{
      numero: string | null; cliente: string | null; importe_usd: number
    }>
  }
  avisos: string[]
  ajustes: AjusteDeCelda[]
  ajustable: boolean
}

export function fetchCobranzaProyectada(
  anio: number, escenario: IdEscenario, pais?: string,
): Promise<CobranzaProyectada> {
  return api.get("/finance/forecast/cobranza", {
    query: { anio, escenario, pais: pais || undefined },
  })
}
